import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Mail, CheckCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { appendUtm } from '../lib/utm';
import { EditableText } from './inline/EditableText';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_REGISTRATION_URL = 'https://utconferences.eventsair.com/cee/registration/Site/Register';

const RegistrationSection = ({ content, sectionId }) => {
  // CMS content can override title and description in the future
  const data = {
    title: content?.title || "Register Your Interest",
    description: content?.description || "Sign up to receive updates and early access to registration.",
    ...content
  };
  const scope = (path) => ({ type: 'homepage_section', sectionId, path });

  const [globalContent, setGlobalContent] = useState(null);
  useEffect(() => {
    let ignore = false;
    fetch(`${API_URL}/api/content/global`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!ignore && d) setGlobalContent(d); })
      .catch(() => {});
    return () => { ignore = true; };
  }, []);
  const registrationUrl = globalContent?.registration_url || DEFAULT_REGISTRATION_URL;

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    organization: '',
    country: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${BACKEND_URL}/api`;
      
      const response = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSubmitted(true);
      toast.success('Thank you! You will receive updates about the World Nuclear Congress.');
      
      setTimeout(() => {
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          organization: '',
          country: '',
        });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to submit form. Please try again.');
    }
  };

  return (
    <section id="registration-section" className="py-20 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        {/* Main Registration CTA */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00457f] mb-5 px-4">
            <EditableText as="span" scope={scope('cta_headline')} value={data.cta_headline} multiline={false}>{data.cta_headline || 'Join Us at the World Nuclear Congress'}</EditableText>
          </h2>
          <p className="text-lg sm:text-xl text-gray-700 mb-8 font-light px-4">
            <EditableText as="span" scope={scope('cta_subheadline')} value={data.cta_subheadline}>{data.cta_subheadline || 'Registration is open. Register now to receive early access rates.'}</EditableText>
          </p>
          
          <div className="flex justify-center px-4 mb-6">
            <Button
              size="lg"
              className="bg-[#00457f] hover:bg-[#00365c] text-white text-base sm:text-lg font-bold px-8 sm:px-12 py-5 sm:py-6 rounded-full transition-all duration-300 transform hover:scale-105 shadow-2xl w-full sm:w-auto"
              onClick={() => {
                window.open(appendUtm(registrationUrl, { utm_medium: 'homepage_cta' }), '_blank');
              }}
            >
              <span className="flex items-center justify-center">
                Official Registration Open
                <ExternalLink className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
              </span>
            </Button>
          </div>
          
          <p className="text-gray-600 text-sm sm:text-base px-4">
            Official registration through the University of Tennessee
          </p>
        </div>

        {/* Stay Informed Card */}
        <Card className="border-2 border-gray-200 shadow-2xl">
          <CardHeader className="bg-gray-50 border-b-2 border-gray-200">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Mail className="w-7 h-7 text-[#00457f]" />
              <CardTitle className="text-2xl font-bold text-[#00457f]">
                Stay Informed
              </CardTitle>
            </div>
            <p className="text-center text-gray-600 text-base">
              Get notified when registration opens and receive event updates
            </p>
          </CardHeader>
          <CardContent className="p-8">
            {isSubmitted ? (
              <div className="text-center py-10 space-y-4">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
                <h3 className="text-2xl font-bold text-green-600">Thank You!</h3>
                <p className="text-lg text-gray-700">
                  You're now subscribed to receive updates about the World Nuclear Congress.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className="text-base font-semibold text-gray-700">
                      First Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      type="text"
                      placeholder="John"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      className="h-12 text-base border-2 focus:border-[#00457f] transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="last_name" className="text-base font-semibold text-gray-700">
                      Last Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      type="text"
                      placeholder="Doe"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      className="h-12 text-base border-2 focus:border-[#00457f] transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-base font-semibold text-gray-700">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="h-12 text-base border-2 focus:border-[#00457f] transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="organization" className="text-base font-semibold text-gray-700">
                      Organization
                    </Label>
                    <Input
                      id="organization"
                      name="organization"
                      type="text"
                      placeholder="Your organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      className="h-12 text-base border-2 focus:border-[#00457f] transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country" className="text-base font-semibold text-gray-700">
                      Country
                    </Label>
                    <Input
                      id="country"
                      name="country"
                      type="text"
                      placeholder="Your country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className="h-12 text-base border-2 focus:border-[#00457f] transition-colors"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-600 hover:from-yellow-600 hover:via-amber-600 hover:to-yellow-700 text-white font-bold text-lg py-6 transition-all duration-300 transform hover:scale-105 relative overflow-hidden"
                  style={{
                    boxShadow: '0 10px 40px rgba(245, 158, 11, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                  }}
                >
                  Sign Up for Updates
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default RegistrationSection;